
import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, X, Info } from 'lucide-react';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '@/components/ui/tooltip';
import { SubscriptionFeature } from '@/hooks/use-subscription';

interface SubscriptionPlansProps {
  plans: SubscriptionFeature[];
  currentPlan?: string;
  onSelectPlan?: (plan: string) => void;
  showCustomerPlans?: boolean;
}

export function SubscriptionPlans({ 
  plans, 
  currentPlan,
  onSelectPlan,
  showCustomerPlans = true 
}: SubscriptionPlansProps) {
  const [selectedPlan, setSelectedPlan] = useState<string | undefined>(currentPlan);

  const handleSelectPlan = (plan: string) => {
    setSelectedPlan(plan);
    if (onSelectPlan) {
      onSelectPlan(plan);
    }
  };

  // Filter plans based on whether we're showing customer or studio plans
  const filteredPlans = showCustomerPlans 
    ? plans.filter(plan => plan.isCustomerPlan)
    : plans.filter(plan => !plan.isCustomerPlan);

  if (filteredPlans.length === 0) {
    return <div>Keine Abonnements verfügbar</div>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 py-6">
      {filteredPlans.map((plan) => (
        <Card 
          key={plan.plan} 
          className={`flex flex-col ${selectedPlan === plan.plan ? 'border-chrona-accent ring-2 ring-chrona-accent' : ''}`}
        >
          <CardHeader>
            <CardTitle className="flex justify-between">
              {getPlanDisplayName(plan.plan)}
              {plan.plan === 'studio_business' && (
                <Badge className="ml-2 bg-chrona-accent">Beliebt</Badge>
              )}
            </CardTitle>
            <CardDescription>
              {getPlanDescription(plan.plan)}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-grow">
            <div className="text-3xl font-bold mb-4">
              {plan.monthlyPrice.toFixed(2)}€
              <span className="text-sm font-normal text-muted-foreground ml-1">/Monat</span>
            </div>
            
            <div className="space-y-2">
              {renderPlanFeature(
                plan.plan === 'free' || plan.plan === 'studio_free' ? 'Kostenlose Basis' : 'Premium Support', 
                true
              )}
              
              {renderPlanFeature(
                renderQuotaText(plan.plan, "appointments"), 
                true
              )}
              
              {!plan.isCustomerPlan && renderPlanFeature(
                renderQuotaText(plan.plan, "staff"), 
                true
              )}
              
              {renderPlanFeature('Kalender-Sync', plan.hasCalendarExport)}
              {renderPlanFeature('Statistik & Dashboard', plan.hasAnalytics)}
              {renderPlanFeature('Add-On Verwaltung', plan.hasAddons)}
              {renderPlanFeature('Referral Tracking', plan.hasReferralBonus)}
              
              {plan.plan === 'studio_premium' && renderPlanFeature('Direkter Support-Kontakt', true)}
              {plan.plan === 'studio_business' && renderPlanFeature('Bevorzugter Support', true)}
              {plan.plan === 'studio_pro' && renderPlanFeature('Standard Support', true)}
            </div>
          </CardContent>
          <CardFooter>
            <Button 
              className="w-full" 
              variant={plan.plan === currentPlan ? "outline" : "default"}
              disabled={plan.plan === currentPlan}
              onClick={() => handleSelectPlan(plan.plan)}
            >
              {plan.plan === currentPlan ? 'Aktueller Plan' : 'Plan wählen'}
            </Button>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function renderPlanFeature(text: string, included: boolean) {
  return (
    <div className="flex items-center">
      {included ? (
        <Check className="h-4 w-4 mr-2 text-green-500" />
      ) : (
        <X className="h-4 w-4 mr-2 text-gray-300" />
      )}
      <span className={included ? '' : 'text-muted-foreground'}>{text}</span>
      
      {text.includes('Quota') && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-4 w-4 ml-2 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent>
              <p>Dies ist die maximale Anzahl pro Monat</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

function getPlanDisplayName(plan: string): string {
  switch (plan) {
    case 'free': return 'Kostenlos';
    case 'pro': return 'Pro';
    case 'studio_free': return 'Studio Free';
    case 'studio_pro': return 'Studio Pro';
    case 'studio_business': return 'Business';
    case 'studio_premium': return 'Premium';
    default: return plan;
  }
}

function getPlanDescription(plan: string): string {
  switch (plan) {
    case 'free': return 'Für gelegentliche Nutzer';
    case 'pro': return 'Für regelmäßige Nutzer';
    case 'studio_free': return 'Für den Einstieg';
    case 'studio_pro': return 'Für kleine Studios';
    case 'studio_business': return 'Für wachsende Studios';
    case 'studio_premium': return 'Für professionelle Studios';
    default: return '';
  }
}

function renderQuotaText(plan: string, type: "appointments" | "staff"): string {
  if (type === "appointments") {
    switch (plan) {
      case 'free':
        return 'Begrenzte Buchungen';
      case 'pro':
        return 'Unbegrenzte Buchungen';
      case 'studio_free':
        return '10 Buchungen/Monat';
      case 'studio_pro':
        return '50 Buchungen/Monat';
      case 'studio_business':
        return '300 Buchungen/Monat';
      case 'studio_premium':
        return 'Unbegrenzte Buchungen';
      default:
        return '';
    }
  } else if (type === "staff") {
    switch (plan) {
      case 'studio_free':
        return '1 Mitarbeiter';
      case 'studio_pro':
        return '3 Mitarbeiter';
      case 'studio_business':
        return '10 Mitarbeiter';
      case 'studio_premium':
        return 'Unbegrenzte Mitarbeiter';
      default:
        return '';
    }
  }
  return '';
}
